import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Target, Award, Users, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
    fetchAboutContent();
    fetchStats();
    
    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.75) {
          setStatsVisible(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/about`);
      setContent(response.data.content);
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0A1A50] to-[#060F2E] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <h1
            className="text-6xl md:text-7xl font-light text-white mb-6"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
              transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
            }}
          >
            {content?.hero_title || 'About Reagle'}
          </h1>
          <p
            className="text-white/90 text-xl md:text-2xl font-light max-w-4xl mx-auto leading-relaxed"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s'
            }}
          >
            {content?.hero_description || 'Leading the way in innovative real estate development solutions'}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section id="stats-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {stats.map((stat, index) => (
              <div
                key={stat.id}
                className="text-center group cursor-default"
                style={{
                  opacity: statsVisible ? 1 : 0,
                  transform: statsVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                  transition: `opacity 0.6s ease-out ${index * 0.2}s, transform 0.6s ease-out ${index * 0.2}s`
                }}
              >
                <div className="text-7xl font-light text-[#0A1A50] mb-4 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-[#B5BBC4] font-light text-lg group-hover:text-gray-800 transition-colors">
                  {stat.label}
                </div>
                <div className="h-1 w-0 group-hover:w-24 bg-[#0A1A50] mx-auto mt-4 transition-all duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-12 transform hover:scale-[1.02] transition-transform duration-500">
            <div className="h-1 w-20 bg-[#0A1A50] mb-8"></div>
            <h2 className="text-5xl font-light text-[#2d2d2d] mb-8">{content?.mission_title || 'Our Mission'}</h2>
            <p className="text-[#B5BBC4] text-2xl leading-relaxed font-light">
              {content?.mission_content || 'To deliver exceptional real estate development services that transform visions into reality'}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-light text-[#2d2d2d] mb-16 text-center">{content?.values_title || 'Our Values'}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(content?.values || []).map((item, index) => {
              const iconMap = { Target, Award, TrendingUp, Users };
              const IconComponent = iconMap[item.icon] || Target;
              return (
              <div
                key={index}
                className="group bg-gray-50 p-8 rounded-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-default"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease-out ${index * 0.15 + 0.5}s, transform 0.6s ease-out ${index * 0.15 + 0.5}s`
                }}
              >
                <div className="w-16 h-16 bg-[#0A1A50]/20 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#0A1A50] group-hover:scale-110 transition-all duration-300">
                  <IconComponent className="w-8 h-8 text-[#0A1A50] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-light text-[#0A1A50] mb-3 group-hover:text-[#060F2E] transition-colors">{item.title}</h3>
                <p className="text-[#B5BBC4] font-light leading-relaxed group-hover:text-gray-800 transition-colors">{item.description}</p>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 bg-gradient-to-br from-[#0A1A50] to-[#060F2E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-20 right-20 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-4">
            {content?.cta_title || 'Join us in creating exceptional real estate experiences'}
          </h2>
        </div>
      </section>
    </div>
  );
};

export default About;